import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connexion,
  deconnexion,
  sessionCourante,
} from '@groupe-trousseau/shared/api-client';
import { CLES } from '../../shared/donnees/cles';
import { deballer, lireLErreur } from '../../shared/api/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA SESSION — ET ELLE EST DE LA DONNÉE SERVEUR, DONC ZUSTAND N'ENTRE PAS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `web/CLAUDE.md` réserve Zustand à « un état réellement global, partagé entre plusieurs
 * features, **et qui n'est pas de la donnée serveur.** En pratique : **la session** et les
 * préférences d'affichage. »
 *
 * **Or la session EST de la donnée serveur sur ce produit**, et pas par accident :
 * `GET /api/v1/auth/session` existe, et D-025 en donne le motif — « la session est un
 * jeton **opaque en base**, jamais un jeton signé », parce que trois règles du corpus
 * exigent une lecture serveur à chaque requête. Le compte, son rôle et ses rattachements
 * actifs **se lisent donc au serveur**, et TanStack Query est exactement l'outil de cela :
 * « **aucune donnée serveur n'est recopiée dans un store client : cela crée deux sources
 * de vérité et des affichages périmés.** »
 *
 * **Ce qui reste d'état purement client est LE JETON**, et il tient dans une variable de
 * `shared/api/client.ts`, qui est le seul fichier autorisé à le connaître. Un magasin
 * global pour une chaîne de caractères lue à un seul endroit serait exactement ce que
 * `ponytail` refuse.
 *
 * **CONCLUSION, ÉCRITE POUR NE PAS SE REDÉCOUVRIR : Zustand n'est pas installé, et le cas
 * que `web/CLAUDE.md` lui réservait ne se présente pas.** Il se présentera peut-être aux
 * préférences d'affichage ; ce jour-là, la décision se trace au journal comme le fichier
 * le demande.
 */

/** Ce que le produit sait de la personne connectée. */
export function useSession() {
  return useQuery({
    queryKey: CLES.session,
    queryFn: () => deballer(sessionCourante()),
    // **On demande toujours, et c'est une conséquence directe du cookie `httpOnly`** :
    // le front **ne peut pas savoir** s'il y a une session — il ne voit pas le cookie.
    // La seule façon de l'apprendre est de demander, et un `401` est la réponse
    // « personne n'est connecté ». Ce n'est pas un appel gaspillé : c'est aussi lui
    // qui **rétablit le jeton anti-CSRF** après un rechargement de page.
    // La session vaut sept jours et son contenu ne change qu'aux gestes du produit :
    // la relire à chaque montage de composant coûterait une requête pour rien.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useConnexion() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (identifiants: { email: string; motDePasse: string }) =>
      deballer(connexion({ body: identifiants })),
    onSuccess: async () => {
      // **Il n'y a plus de jeton à ranger** : le serveur a posé le cookie, le
      // navigateur le tient, et personne ici ne l'a vu passer. Il ne reste qu'à
      // relire la session, qui dira qui est entré.
      await cache.invalidateQueries({ queryKey: CLES.session });
    },
  });
}

export function useDeconnexion() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // **La déconnexion est une RÉVOCATION côté serveur**, pas un oubli côté client :
      // « le jeton cesse immédiatement de valoir ». Oublier le jeton sans appeler la
      // route laisserait une session vivante sept jours.
      await deballer(deconnexion());
    },
    // **`onSettled` et non `onSuccess`** : si l'appel échoue, la personne a quand même
    // voulu partir. Le cookie, lui, n'est effacé que par le serveur — le front ne
    // peut pas retirer un cookie `httpOnly`, et c'est voulu : **une déconnexion qui
    // n'a pas révoqué la session ne doit pas en avoir l'air.** Ce qui se vide ici est
    // le cache local, pour qu'aucune donnée de la personne partie ne reste à l'écran.
    onSettled: () => {
      cache.clear();
    },
  });
}

export { lireLErreur };
